import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { CMRSigningDataInDTO, CmrSigningImageOutDTO, CompanyLocationCmrSigningInDTO, CompanyLocationsDetailOutDTO, FeaturesEnum, FileV2InDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import Card from '@components/framework/card/Card.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import UploadPanelVisual from '@components/framework/uploadpanel/UploadPanelVisual.tsx';
import COItemDisplay from '@app/pages/COItemDisplay/COItemDisplay.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import SvgDocument from '@app/pages/attachment/Document.tsx';
import SvgDeleteIcon from '@app/pages/attachment/DeleteIcon.tsx';
import SvgGrayInfo from '@components/ringil3/icons/GrayInfo.tsx';
import SvgTabAttachments from '@components/ringil3/icons/TabAttachments.tsx';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import { Field } from '@components/framework/formfield/Field.tsx';
import LocationSidebarHeader from '@components/RingilComponents/LocationSidebarHeader/LocationSidebarHeader.tsx';
import { InputText } from 'primereact/inputtext';
import { isNotBlank } from '@utils/utils.tsx';

type SigningSide = 'sender' | 'receiver';

interface ComponentProps {
  location: CompanyLocationsDetailOutDTO;
  onReload: () => void;
}

interface CmrCardProps {
  location: CompanyLocationsDetailOutDTO;
  side: SigningSide;
  onEdit: () => void;
  /** A deleted location is read-only everywhere — Restore on the detail is the only action offered. */
  readOnly?: boolean;
}

const CmrCard: React.FC<CmrCardProps> = ({ location, side, onEdit, readOnly }) => {
  const { tr } = useTranslationLgs();
  const { getCmrSigningImageSender, getCmrSigningImageReceiver } = useContext(ApiContext);
  const enabled = side === 'sender' ? location.senderSigningEnabled : location.receiverSigningEnabled;
  const signingData = side === 'sender' ? location.senderSigningData : location.receiverSigningData;
  const [image, setImage] = useState<CmrSigningImageOutDTO>(null);

  useEffect(() => {
    // BE returns a validation error when no image is stored, so only fetch with a stored fileName
    if (enabled && isNotBlank(signingData?.imageDataFileName)) {
      const getImage = side === 'sender' ? getCmrSigningImageSender : getCmrSigningImageReceiver;
      getImage(location.id, data => setImage(data));
    } else {
      setImage(null);
    }
  }, [location, side]);

  const heading =
    side === 'sender'
      ? tr('LocalityDetail.senderSigning', 'Sender signature from the sending location')
      : tr('LocalityDetail.receiverSigning', 'Recipient signature at the target location');
  const caption =
    side === 'sender'
      ? tr('LocalityDetail.cmrSenderFieldCaption', '[22] Signature and stamp of the sender')
      : tr('LocalityDetail.cmrReceiverFieldCaption', '[24] Date and place of receipt');
  const disabledMessage =
    side === 'sender'
      ? tr('LocalityDetail.senderSigningDisabled', 'Sender signing is not allowed.')
      : tr('LocalityDetail.receiverSigningDisabled', 'Receiver signing is not allowed.');

  return (
    <Card>
      <div className={'flex flex-row items-start justify-between gap-4'}>
        <COHeadline variant={'h3'} title={heading} />
        {!readOnly && (
          <RingilButton
            mode={'table'}
            icon={<SvgPencil />}
            onClick={onEdit}
            dataTestId={`location-cmr-${side}-edit`}
          />
        )}
      </div>
      {enabled ? (
        <div className={'flex flex-col gap-2 mt-4'}>
          <span className={'text-sm text-content-secondary'}>{tr('LocalityDetail.preview', 'Preview')}</span>
          <div className={'border border-solid border-content-primary bg-base-base p-3 min-h-[100px] max-w-[420px]'}>
            <span className={'font-medium text-sm text-content-primary'}>{caption}</span>{' '}
            <span className={'text-content-tertiary text-sm'}>/ {signingData?.textEnabled && isNotBlank(signingData?.text) ? signingData.text : 'Label'}</span>
            {image && <img src={`data:${image.mimeType};base64,${image.data}`} alt={image.fileName} className={'block mt-2 max-h-20 max-w-full'} />}
          </div>
        </div>
      ) : (
        <div className={'flex flex-row items-center gap-3 mt-4 rounded-lg bg-base-base px-4 py-3'}>
          <SvgGrayInfo className={'shrink-0'} />
          <span className={'text-content-secondary grow'}>{disabledMessage}</span>
          {!readOnly && (
            <RingilButton
              mode={'outlined'}
              label={tr('generic.change', 'Change')}
              onClick={onEdit}
              dataTestId={`location-cmr-${side}-change`}
            />
          )}
        </div>
      )}
    </Card>
  );
};

interface CmrSigningSidebarProps {
  location: CompanyLocationsDetailOutDTO;
  side: SigningSide;
  visible: boolean;
  onHide: () => void;
  onSaved: () => void;
}

const CmrSigningSidebar: React.FC<CmrSigningSidebarProps> = ({ location, side, visible, onHide, onSaved }) => {
  const { tr } = useTranslationLgs();
  const { updateLocationCmrSigningSender, updateLocationCmrSigningReceiver } = useContext(ApiContext);
  const inputFileRef = useRef<HTMLInputElement>(null);

  const existingData = side === 'sender' ? location.senderSigningData : location.receiverSigningData;

  const [enabled, setEnabled] = useState(false);
  const [userEnabled, setUserEnabled] = useState(false);
  const [textEnabled, setTextEnabled] = useState(false);
  const [text, setText] = useState('');
  const [imageEnabled, setImageEnabled] = useState(false);
  const [newImage, setNewImage] = useState<FileV2InDTO>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setEnabled(side === 'sender' ? location.senderSigningEnabled : location.receiverSigningEnabled);
      setUserEnabled(existingData?.userEnabled ?? false);
      setTextEnabled(existingData?.textEnabled ?? false);
      setText(existingData?.text ?? '');
      setImageEnabled(existingData?.imageEnabled ?? false);
      setNewImage(null);
      setSaving(false);
    }
  }, [visible, location, side]);

  const processFiles = (fileList: File[]) => {
    if (fileList?.length > 0) {
      const file = fileList[0];
      const reader = new FileReader();
      reader.onload = e => {
        setNewImage({ fileName: file.name, dataURL: e.target?.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const save = () => {
    setSaving(true);
    const signingData: CMRSigningDataInDTO = {
      textEnabled,
      text: isNotBlank(text) ? text : null,
      imageEnabled,
      userEnabled,
      updateImage: newImage != null,
      imageData: newImage ?? undefined,
    };
    const body: CompanyLocationCmrSigningInDTO = {
      enabled,
      data: enabled ? signingData : undefined,
    };
    const update = side === 'sender' ? updateLocationCmrSigningSender : updateLocationCmrSigningReceiver;
    update(
      location.id,
      body,
      () => {
        setSaving(false);
        onSaved();
      },
      null,
      {
        onError: (error, showStandardError) => {
          showStandardError();
          setSaving(false);
        },
      },
    );
  };

  return (
    <RingilSidebar
      visible={visible}
      heading={side === 'sender' ? tr('LocalityDetail.senderSignature', 'Sender signature') : tr('LocalityDetail.receiverSignature', 'Recipient signature')}
      onBack={onHide}
      onContinue={save}
      loading={saving}
      dataTestIdContinue={`location-cmr-${side}-save`}
      dataTestIdBack={`location-cmr-${side}-back`}
    >
      <div className={'flex flex-col gap-6'}>
        <LocationSidebarHeader locationId={location.id} variant={'locality'} />

        <RingilInputSwitch
          label={
            side === 'sender'
              ? tr('AddEditLocationSidebar.senderSigningEnabled', 'Sender signing enabled')
              : tr('AddEditLocationSidebar.receiverSigningEnabled', 'Receiver signing enabled')
          }
          value={enabled}
          onChange={setEnabled}
          dataTestId={`location-cmr-${side}-enabled`}
        />

        {enabled && (
          <>
            <COHeadline variant={'h4'} title={tr('LocalityDetail.signatureSettings', 'Signature settings')} />
            <RingilInputSwitch
              label={
                side === 'sender'
                  ? tr('AddEditLocationSidebar.senderSigningUserEnabled', 'Sender user signing enabled')
                  : tr('AddEditLocationSidebar.receiverSigningUserEnabled', 'Receiver user signing enabled')
              }
              value={userEnabled}
              onChange={setUserEnabled}
              dataTestId={`location-cmr-${side}-userEnabled`}
            />
            <RingilInputSwitch
              label={
                side === 'sender'
                  ? tr('AddEditLocationSidebar.senderSigningTextEnabled', 'Sender text signing enabled')
                  : tr('AddEditLocationSidebar.receiverSigningTextEnabled', 'Receiver text signing enabled')
              }
              value={textEnabled}
              onChange={setTextEnabled}
              dataTestId={`location-cmr-${side}-textEnabled`}
            />
            {textEnabled && (
              <Field label={tr('LocalityDetail.signingText', 'Text')}>
                <InputText value={text} onChange={e => setText(e.target.value)} data-testid={`location-cmr-${side}-text`} />
              </Field>
            )}
            <RingilInputSwitch
              label={
                side === 'sender'
                  ? tr('AddEditLocationSidebar.senderSigningImageEnabled', 'Sender image signing enabled')
                  : tr('AddEditLocationSidebar.receiverSigningImageEnabled', 'Receiver image signing enabled')
              }
              value={imageEnabled}
              onChange={setImageEnabled}
              dataTestId={`location-cmr-${side}-imageEnabled`}
            />
            {imageEnabled && (
              <>
                {newImage ? (
                  <COItemDisplay icon={<SvgDocument />} label={newImage.fileName} rightIcon={<SvgDeleteIcon className={'cursor-pointer'} onClick={() => setNewImage(null)} />} />
                ) : (
                  <>
                    {existingData?.imageDataFileName && <COItemDisplay icon={<SvgDocument />} label={existingData.imageDataFileName} />}
                    <UploadPanelVisual onFiles={processFiles} onClick={() => inputFileRef.current?.click()} showDropZone />
                    <input type="file" ref={inputFileRef} onChange={e => processFiles(Array.from(e.target.files ?? []))} className="hidden" />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </RingilSidebar>
  );
};

const CmrSection: React.FC<ComponentProps> = ({ location, onReload }) => {
  const { tr } = useTranslationLgs();
  const { hasFeature } = useContext(AuthenticatedUserContext);
  const [editSide, setEditSide] = useState<SigningSide>(null);

  if (!hasFeature([FeaturesEnum.SIGNING_CMR])) return null;

  return (
    <>
      <SectionPanel
        icon={<SvgTabAttachments viewBox={'0 0 16 16'} className={'w-6 h-6 [&_path]:fill-content-tertiary'} />}
        heading={tr('LocalityDetail.tabCmr', 'CMR')}
        dataTestId={'location-cmr-section'}>
        <div className={'grid grid-cols-1 lg:grid-cols-2 gap-4 items-start'}>
          <CmrCard location={location} side={'sender'} onEdit={() => setEditSide('sender')} readOnly={location.isDeleted} />
          <CmrCard location={location} side={'receiver'} onEdit={() => setEditSide('receiver')} readOnly={location.isDeleted} />
        </div>
      </SectionPanel>
      {editSide && (
        <CmrSigningSidebar
          location={location}
          side={editSide}
          visible={true}
          onHide={() => setEditSide(null)}
          onSaved={() => {
            setEditSide(null);
            onReload();
          }}
        />
      )}
    </>
  );
};

export default CmrSection;
